/**
 * Tools Bookings page for endusers
 * Uses shared booking components from lawyer dashboard
 */
import { createRoute, useNavigate } from '@tanstack/react-router'
import { useState, useEffect, useCallback } from 'react'
import { Plus, RefreshCw, Plane, AlertCircle, ArrowLeft } from 'lucide-react'
import { toolsRoute } from './index'
import { lawyerBookingsAPI } from '@/utils/api'
import { BookingSearch } from '@/components/lawyer/BookingSearch'
import { BookingsTable } from '@/components/lawyer/BookingsTable'
import { BookingLookupForm } from '@/components/lawyer/BookingLookupForm'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'

function ToolsBookingsPage() {
  const navigate = useNavigate()
  const [bookings, setBookings] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [showLookupModal, setShowLookupModal] = useState(false)
  const [isLookingUp, setIsLookingUp] = useState(false)
  const [error, setError] = useState(null)

  const loadBookings = useCallback(async (showRefreshIndicator = false) => {
    if (showRefreshIndicator) setIsRefreshing(true)

    try {
      const response = await lawyerBookingsAPI.getBookings()
      setBookings(response.bookings || [])
      setError(null)
    } catch (err) {
      console.error('Failed to load bookings:', err)
      setError('Buchungen konnten nicht geladen werden. Bitte erneut versuchen.')
    } finally {
      setIsLoading(false)
      setIsRefreshing(false)
    }
  }, [])

  useEffect(() => {
    loadBookings()
  }, [loadBookings])

  const handleRemoveBooking = async (accessId) => {
    try {
      await lawyerBookingsAPI.removeBooking(accessId)
      setBookings((prev) => prev.filter((b) => b.id !== accessId))
      toast.success('Buchung entfernt', {
        description: 'Buchung wurde aus Ihrer Liste entfernt.',
      })
    } catch (err) {
      console.error('Failed to remove booking:', err)
      toast.error('Buchung konnte nicht entfernt werden', {
        description: err.response?.data?.message || 'Bitte erneut versuchen.',
      })
    }
  }

  const handleLookup = async (data) => {
    setIsLookingUp(true)
    try {
      const response = await lawyerBookingsAPI.lookupBooking(
        data.pnr,
        data.lastname,
        {
          enableMonitoring: data.enableMonitoring,
          monitoringFrequency: data.monitoringFrequency,
          monitoringUntil: data.monitoringUntil,
        }
      )

      setShowLookupModal(false)

      if (response.status === 'success' && response.has_data) {
        toast.success('Buchung gefunden!', {
          description: `PNR ${data.pnr} ist jetzt in Ihrer Liste`,
        })
        navigate({ to: `/tools/bookings/${response.access_id}` })
      } else {
        toast.info('Buchungsabfrage gestartet', {
          description: 'Buchungsdaten werden abgerufen. Dies kann einen Moment dauern.',
        })
        // Reload the list to show the new booking
        loadBookings()
        // Navigate to the booking detail (it will show loading state)
        if (response.access_id) {
          navigate({ to: `/tools/bookings/${response.access_id}` })
        }
      }
    } catch (err) {
      console.error('Lookup failed:', err)
      const message = err.response?.data?.errors || err.response?.data?.message || 'Buchung konnte nicht abgerufen werden'
      toast.error('Abfrage fehlgeschlagen', {
        description: typeof message === 'string' ? message : JSON.stringify(message),
      })
    } finally {
      setIsLookingUp(false)
    }
  }

  const unreadCount = bookings.filter((b) => b.has_unread_changes).length

  return (
    <div className="max-w-6xl mx-auto">
      {/* Back button */}
      <button
        onClick={() => navigate({ to: '/tools' })}
        className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Zurück zur Übersicht
      </button>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">
            Buchungen
          </h1>
          <p className="text-slate-500 mt-1">
            Lufthansa-Buchungen suchen und überwachen
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadBookings(true)}
            disabled={isRefreshing}
            className="gap-2"
          >
            <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
            Aktualisieren
          </Button>
          <Button
            onClick={() => setShowLookupModal(true)}
            className="gap-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white"
          >
            <Plus className="h-4 w-4" />
            Neue Abfrage
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-8">
        <BookingSearch className="max-w-2xl" baseRoute="/tools/bookings" />
      </div>

      {/* Stats Bar */}
      {bookings.length > 0 && (
        <div className="flex items-center gap-6 mb-6 text-sm">
          <span className="text-slate-500">
            <span className="font-semibold text-slate-900">{bookings.length}</span> {bookings.length !== 1 ? 'Buchungen' : 'Buchung'}
          </span>
          {unreadCount > 0 && (
            <span className="text-red-600">
              <span className="font-semibold">{unreadCount}</span> mit ungelesenen Änderungen
            </span>
          )}
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-4">
            <div className="h-10 w-10 border-4 border-slate-200 border-t-blue-500 rounded-full animate-spin" />
            <p className="text-slate-500">Buchungen werden geladen...</p>
          </div>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
          <p className="text-slate-900 font-medium">{error}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadBookings()}
            className="mt-4"
          >
            Erneut versuchen
          </Button>
        </div>
      ) : bookings.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <div className="h-20 w-20 rounded-full bg-slate-100 flex items-center justify-center mb-4">
            <Plane className="h-10 w-10 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            Noch keine Buchungen
          </h3>
          <p className="text-slate-500 max-w-sm mb-6">
            Geben Sie eine PNR und einen Nachnamen ein, um eine Buchung abzurufen.
          </p>
          <Button
            onClick={() => setShowLookupModal(true)}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Buchung abrufen
          </Button>
        </div>
      ) : (
        <BookingsTable bookings={bookings} onRemove={handleRemoveBooking} baseRoute="/tools/bookings" />
      )}

      {/* Lookup Modal */}
      <Dialog open={showLookupModal} onOpenChange={setShowLookupModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Buchung abrufen</DialogTitle>
            <DialogDescription>
              Geben Sie die PNR und den Nachnamen ein, um eine Lufthansa-Buchung abzurufen.
            </DialogDescription>
          </DialogHeader>
          <BookingLookupForm onSubmit={handleLookup} isLoading={isLookingUp} />
        </DialogContent>
      </Dialog>
    </div>
  )
}

export const toolsBookingsRoute = createRoute({
  getParentRoute: () => toolsRoute,
  path: '/bookings',
  component: ToolsBookingsPage,
})
